import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import axios from "axios";
import {
  Pen, Eraser, Undo2, Redo2, Trash2, Save, FileText, Loader2,
  ChevronLeft, ChevronRight, Plus, Type, Wifi, WifiOff,
  Send, ClipboardCopy, BookOpen, Download, Layers, ScanLine, X,
  Play, Pause, CheckCircle2, AlertTriangle, RefreshCw, Eye,
  ThumbsUp, ThumbsDown, Edit3, Link, Shield, Clock, Zap,
  ChevronDown, ChevronUp, MessageSquare, Mic, MicOff, Square
} from "lucide-react";

/* ─── IndexedDB Offline Store ─────────────────────────────────── */
const IDB_NAME = "charstan_scriber_m1";
const IDB_VERSION = 2;
const STORES = { pages: "pages", sessions: "sessions", inputs: "inputs", syncQueue: "sync_queue" };

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      Object.values(STORES).forEach(name => {
        if (!db.objectStoreNames.contains(name)) {
          const keyPath = name === "pages" ? "page_id" : name === "sessions" ? "session_id" : name === "inputs" ? "input_id" : "queue_id";
          db.createObjectStore(name, { keyPath });
        }
      });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(storeName, data) {
  const db = await openDB();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).put(data);
  return new Promise((r, e) => { tx.oncomplete = r; tx.onerror = e; });
}

async function idbGetAll(storeName, filterFn) {
  const db = await openDB();
  const tx = db.transaction(storeName, "readonly");
  return new Promise((resolve) => {
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(filterFn ? (req.result || []).filter(filterFn) : (req.result || []));
    req.onerror = () => resolve([]);
  });
}

async function idbDelete(storeName, key) {
  const db = await openDB();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).delete(key);
}

/* ─── Helpers ─────────────────────────────────────────────────── */
const uid = () => `scr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const PEN_COLORS = ["#FFFFFF", "#3B82F6", "#EF4444", "#22C55E", "#F59E0B", "#A855F7"];
const PEN_SIZES = [2, 4, 6, 10];
const ERASER_SIZE = 20;

/* ─── Form Template Presets ─────────────────────────────────── */
const FORM_TEMPLATES = {
  blank: { id: "blank", name: "Blank Audit Form", desc: "Generic audit form", fields: [
    { label: "Audit ID / Reference:", y: 40, h: 50 }, { label: "Date:", y: 100, h: 50 },
    { label: "Auditor:", y: 160, h: 50 }, { label: "Location / Dept:", y: 220, h: 50 },
    { label: "Observation / Finding:", y: 290, h: 120 }, { label: "Evidence / Notes:", y: 420, h: 120 },
    { label: "Action Required:", y: 550, h: 80 },
  ]},
  nonconformance: { id: "nonconformance", name: "NCR Form", desc: "Nonconformance report", fields: [
    { label: "NCR Number:", y: 36, h: 36 }, { label: "Date Identified:", y: 80, h: 36 },
    { label: "Originator / Auditor:", y: 124, h: 36 }, { label: "Department / Area:", y: 168, h: 36 },
    { label: "Standard / Procedure Ref:", y: 212, h: 36 },
    { label: "Classification:  [ ] Critical  [ ] Major  [ ] Minor", y: 256, h: 36 },
    { label: "Description of Nonconformance:", y: 300, h: 100 }, { label: "Objective Evidence:", y: 408, h: 80 },
  ]},
  checklist: { id: "checklist", name: "Audit Checklist", desc: "Pass/fail items", fields: [
    { label: "Audit Title:", y: 36, h: 36 }, { label: "Standard / Regulation:", y: 80, h: 36 },
    { label: "1. [ ] Pass  [ ] Fail  [ ] NA", y: 134, h: 50 }, { label: "2. [ ] Pass  [ ] Fail  [ ] NA", y: 192, h: 50 },
    { label: "3. [ ] Pass  [ ] Fail  [ ] NA", y: 250, h: 50 }, { label: "4. [ ] Pass  [ ] Fail  [ ] NA", y: 308, h: 50 },
    { label: "5. [ ] Pass  [ ] Fail  [ ] NA", y: 366, h: 50 },
    { label: "Overall Assessment:", y: 430, h: 60 }, { label: "Auditor Signature:", y: 498, h: 50 },
  ]},
};

/* Status badge colors */
const STATUS_COLORS = {
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  paused: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  closed: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

/* ═══════════════════════════════════════════════════════════════ */

const ScriberPage = () => {
  const { t } = useTranslation('scriber');
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  /* ── Core state ── */
  const [view, setView] = useState("sessions"); // sessions | workspace
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ── Disclosure ── */
  const [disclosureState, setDisclosureState] = useState({ required: false, accepted: false, text: "", version: "" });
  const [showDisclosure, setShowDisclosure] = useState(false);

  /* ── Workspace: Pages & Drawing ── */
  const [pages, setPages] = useState([]);
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState("pen");
  const [penColor, setPenColor] = useState("#FFFFFF");
  const [penSize, setPenSize] = useState(4);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  /* ── Workspace: Typed Notes ── */
  const [typedNote, setTypedNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  /* ── Workspace: Mode ── */
  const [mode, setMode] = useState("whiteboard");
  const [formTemplate, setFormTemplate] = useState("blank");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  /* ── Sync ── */
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState("local");
  const [syncSummary, setSyncSummary] = useState(null);

  /* ── Extractions ── */
  const [extractions, setExtractions] = useState([]);
  const [showExtractions, setShowExtractions] = useState(false);
  const [extracting, setExtracting] = useState(false);

  /* ── Transcription ── */
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [showTranscription, setShowTranscription] = useState(false);

  /* ── Voice Recording (M3) ── */
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceTranscribing, setVoiceTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  /* ── Audit Context (M3) ── */
  const [auditContext, setAuditContext] = useState(null);

  /* ── Online/Offline listener ── */
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  /* ── Load disclosure + sessions on mount ── */
  useEffect(() => {
    loadDisclosureStatus();
    loadSessions();
  }, []);

  const loadDisclosureStatus = async () => {
    try {
      const res = await axios.get(`${API}/scriber/disclosures/status`, { withCredentials: true });
      setDisclosureState(res.data);
      if (res.data.required && !res.data.accepted) setShowDisclosure(true);
    } catch (e) {
      console.error("Disclosure check failed:", e);
    }
  };

  const acceptDisclosure = async () => {
    try {
      await axios.post(`${API}/scriber/disclosures/accept`, {
        disclosure_version: disclosureState.current_version || "1.0", accepted: true,
      }, { withCredentials: true });
      setDisclosureState(prev => ({ ...prev, accepted: true }));
      setShowDisclosure(false);
    } catch (e) { console.error("Disclosure accept failed:", e); }
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/scriber/sessions`, { withCredentials: true });
      setSessions(res.data.sessions || []);
    } catch (e) {
      const local = await idbGetAll(STORES.sessions);
      setSessions(local);
    } finally { setLoading(false); }
  };

  /* ── Session actions ── */
  const createSession = async (label, modeVal, context) => {
    if (disclosureState.required && !disclosureState.accepted) {
      setShowDisclosure(true);
      return;
    }
    try {
      const res = await axios.post(`${API}/scriber/sessions`, {
        label, mode: modeVal || "whiteboard", workflow_context: context || {},
      }, { withCredentials: true });
      const session = res.data;
      await idbPut(STORES.sessions, session);
      setActiveSession(session);
      initWorkspace(session);
      setView("workspace");
    } catch (e) {
      console.error("Create session failed:", e);
      alert(e.response?.data?.detail || "Failed to create session");
    }
  };

  const openSession = async (session) => {
    if (session.status === "completed" || session.status === "closed") {
      // Read-only view
    }
    setActiveSession(session);
    initWorkspace(session);
    await loadExtractions(session.session_id);
    setView("workspace");
  };

  const pauseSession = async () => {
    if (!activeSession) return;
    try {
      const res = await axios.post(`${API}/scriber/sessions/${activeSession.session_id}/pause`, {}, { withCredentials: true });
      setActiveSession(res.data);
    } catch (e) { console.error("Pause failed:", e); }
  };

  const resumeSession = async () => {
    if (!activeSession) return;
    try {
      const res = await axios.post(`${API}/scriber/sessions/${activeSession.session_id}/resume`, {}, { withCredentials: true });
      setActiveSession(res.data);
    } catch (e) { console.error("Resume failed:", e); }
  };

  const completeSession = async () => {
    if (!activeSession) return;
    if (!window.confirm("Mark this session as completed? You won't be able to add new inputs.")) return;
    try {
      const res = await axios.post(`${API}/scriber/sessions/${activeSession.session_id}/complete`, {}, { withCredentials: true });
      setActiveSession(res.data);
    } catch (e) { console.error("Complete failed:", e); }
  };

  /* ── Workspace init ── */
  const initWorkspace = (session) => {
    const firstPage = {
      page_id: uid(), session_id: session.session_id, page_number: 1,
      mode: session.mode || "whiteboard", strokes: [], image_data: "",
      transcription: "", created_at: new Date().toISOString(),
    };
    setPages([firstPage]);
    setCurrentPageIdx(0);
    setMode(session.mode || "whiteboard");
    setTypedNote("");
    setExtractions([]);
    setShowExtractions(false);
    setShowTranscription(false);
    setUndoStack([]);
    setRedoStack([]);
    setSyncStatus("local");
    idbPut(STORES.pages, firstPage);
  };

  /* ── Canvas setup ── */
  useEffect(() => {
    if (view !== "workspace") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
    redrawCanvas();
  }, [currentPageIdx, pages.length, view]);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    ctx.fillStyle = "#0D1F38";
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    ctx.strokeStyle = "#1E3A5F22";
    ctx.lineWidth = 0.5;
    for (let y = 0; y < canvas.offsetHeight; y += 32) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.offsetWidth, y); ctx.stroke();
    }
    if (mode === "form") {
      const tpl = FORM_TEMPLATES[formTemplate] || FORM_TEMPLATES.blank;
      ctx.font = "bold 13px monospace"; ctx.fillStyle = "#64748B";
      ctx.fillText(tpl.name.toUpperCase(), 16, 22); ctx.font = "11px monospace";
      for (const f of tpl.fields) {
        ctx.fillText(f.label, 16, f.y - 4);
        ctx.strokeStyle = "#1E3A5F44"; ctx.lineWidth = 1;
        ctx.strokeRect(12, f.y, canvas.offsetWidth / 2 - 24, f.h);
      }
    }
    const page = pages[currentPageIdx];
    if (page?.strokes) {
      for (const stroke of page.strokes) {
        if (stroke.points.length < 2) continue;
        ctx.strokeStyle = stroke.color; ctx.lineWidth = stroke.size;
        ctx.globalCompositeOperation = stroke.eraser ? "destination-out" : "source-over";
        ctx.beginPath(); ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        for (let i = 1; i < stroke.points.length; i++) ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        ctx.stroke(); ctx.globalCompositeOperation = "source-over";
      }
    }
  }, [currentPageIdx, pages, mode, formTemplate]);

  useEffect(() => { redrawCanvas(); }, [redrawCanvas]);

  /* ── Drawing handlers ── */
  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left, y: (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top, pressure: e.pressure ?? 0.5 };
  };

  const startDraw = (e) => {
    if (activeSession?.status !== "active") return;
    e.preventDefault();
    const ctx = ctxRef.current; if (!ctx) return;
    const point = getPoint(e);
    setUndoStack(prev => [...prev, canvasRef.current.toDataURL()]); setRedoStack([]);
    ctx.strokeStyle = tool === "eraser" ? "#0D1F38" : penColor;
    ctx.lineWidth = tool === "eraser" ? ERASER_SIZE : penSize;
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
    ctx.beginPath(); ctx.moveTo(point.x, point.y);
    setIsDrawing(true);
    setPages(prev => {
      const updated = [...prev]; const page = { ...updated[currentPageIdx] };
      page.strokes = [...(page.strokes || []), { color: penColor, size: tool === "eraser" ? ERASER_SIZE : penSize, eraser: tool === "eraser", points: [point] }];
      updated[currentPageIdx] = page; return updated;
    });
  };

  const draw = (e) => {
    if (!isDrawing) return; e.preventDefault();
    const ctx = ctxRef.current; const point = getPoint(e);
    ctx.lineTo(point.x, point.y); ctx.stroke();
    setPages(prev => {
      const updated = [...prev]; const page = { ...updated[currentPageIdx] };
      const strokes = [...page.strokes]; const lastStroke = { ...strokes[strokes.length - 1] };
      lastStroke.points = [...lastStroke.points, point]; strokes[strokes.length - 1] = lastStroke;
      page.strokes = strokes; updated[currentPageIdx] = page; return updated;
    });
  };

  const endDraw = () => {
    if (!isDrawing) return;
    if (ctxRef.current) ctxRef.current.globalCompositeOperation = "source-over";
    setIsDrawing(false); setSyncStatus("local");
    const page = pages[currentPageIdx];
    if (page) idbPut(STORES.pages, { ...page, image_data: canvasRef.current?.toDataURL("image/png") || "" });
  };

  const undo = () => {
    if (undoStack.length === 0) return;
    setRedoStack(prev => [...prev, canvasRef.current.toDataURL()]);
    const prevState = undoStack[undoStack.length - 1]; setUndoStack(prev => prev.slice(0, -1));
    const img = new Image(); img.onload = () => { ctxRef.current?.clearRect(0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight); ctxRef.current?.drawImage(img, 0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight); }; img.src = prevState;
    setPages(prev => { const updated = [...prev]; updated[currentPageIdx] = { ...updated[currentPageIdx], strokes: updated[currentPageIdx].strokes.slice(0, -1) }; return updated; });
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    setUndoStack(prev => [...prev, canvasRef.current.toDataURL()]);
    const nextState = redoStack[redoStack.length - 1]; setRedoStack(prev => prev.slice(0, -1));
    const img = new Image(); img.onload = () => { ctxRef.current?.clearRect(0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight); ctxRef.current?.drawImage(img, 0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight); }; img.src = nextState;
  };

  /* ── Page management ── */
  const addPage = () => {
    const newPage = { page_id: uid(), session_id: activeSession?.session_id, page_number: pages.length + 1, mode, strokes: [], image_data: "", transcription: "", created_at: new Date().toISOString() };
    setPages(prev => [...prev, newPage]); setCurrentPageIdx(pages.length);
    setUndoStack([]); setRedoStack([]); idbPut(STORES.pages, newPage);
  };
  const goPage = (idx) => { setCurrentPageIdx(idx); setUndoStack([]); setRedoStack([]); };
  const clearPage = () => {
    setUndoStack(prev => [...prev, canvasRef.current?.toDataURL()]);
    setRedoStack([]);
    setPages(prev => { const updated = [...prev]; updated[currentPageIdx] = { ...updated[currentPageIdx], strokes: [] }; return updated; });
    setSyncStatus("local");
  };

  /* ── Typed note save ── */
  const saveTypedNote = async () => {
    if (!typedNote.trim() || !activeSession) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/scriber/sessions/${activeSession.session_id}/inputs`, {
        input_type: "text", data: typedNote.trim(), field_section: "notes",
      }, { withCredentials: true });
      setTypedNote("");
      setActiveSession(prev => ({ ...prev, input_count: (prev?.input_count || 0) + 1 }));
    } catch (e) {
      // Save to IndexedDB if offline
      const localInput = { input_id: uid(), session_id: activeSession.session_id, input_type: "text", data: typedNote.trim(), field_section: "notes", created_at: new Date().toISOString() };
      await idbPut(STORES.inputs, localInput);
      setTypedNote("");
    } finally { setSavingNote(false); }
  };

  /* ── Sync to server ── */
  const syncToServer = async () => {
    if (!online || !activeSession) return;
    setSyncing(true); setSyncStatus("syncing");
    try {
      const localInputs = await idbGetAll(STORES.inputs, i => i.session_id === activeSession.session_id);
      if (localInputs.length > 0) {
        const items = localInputs.map(inp => ({ item_type: "input", item_id: inp.input_id, data: inp, client_timestamp: inp.created_at }));
        const res = await axios.post(`${API}/scriber/sync/push`, { items }, { withCredentials: true });
        setSyncSummary(res.data);
      }
      const statusRes = await axios.get(`${API}/scriber/sync/status`, { withCredentials: true });
      setSyncSummary(prev => ({ ...(prev || {}), ...statusRes.data }));
      setSyncStatus("synced");
    } catch (e) {
      console.error("Sync failed:", e); setSyncStatus("local");
    } finally { setSyncing(false); }
  };

  /* ── Extraction ── */
  const runExtraction = async () => {
    if (!activeSession) return;
    setExtracting(true); setShowExtractions(true);
    try {
      const res = await axios.post(`${API}/scriber/sessions/${activeSession.session_id}/extractions/run`, {
        context_hint: activeSession.label || "Field audit notes",
      }, { withCredentials: true });
      setExtractions(prev => [res.data, ...prev]);
    } catch (e) {
      console.error("Extraction failed:", e);
      alert(e.response?.data?.detail || "Extraction failed");
    } finally { setExtracting(false); }
  };

  const loadExtractions = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/scriber/sessions/${sessionId}/extractions`, { withCredentials: true });
      setExtractions(res.data.extractions || []);
    } catch (e) { console.error("Load extractions failed:", e); }
  };

  const reviewExtraction = async (extractionId, status, editedValue) => {
    try {
      const payload = { status };
      if (status === "edited" && editedValue) payload.edited_value = editedValue;
      const res = await axios.put(`${API}/scriber/extractions/${extractionId}/review`, payload, { withCredentials: true });
      setExtractions(prev => prev.map(e => e.extraction_id === extractionId ? res.data : e));
    } catch (e) { console.error("Review failed:", e); }
  };

  /* ── Transcribe ── */
  const transcribePage = async () => {
    const canvas = canvasRef.current; if (!canvas) return;
    setTranscribing(true); setShowTranscription(true); setTranscription("");
    try {
      const imageData = canvas.toDataURL("image/png");
      // Save as handwriting input
      if (activeSession) {
        await axios.post(`${API}/scriber/sessions/${activeSession.session_id}/inputs`, {
          input_type: "handwriting", data: imageData.split(",")[1]?.substring(0, 100) || "", // metadata only
          metadata: { source: "canvas", page_number: currentPageIdx + 1, mode },
        }, { withCredentials: true });
      }
      // GPT-4o transcription is handled via existing /scriber/sessions/{id}/extractions/run
      setTranscription("Handwriting captured. Use 'Extract' to process with AI.");
    } catch (e) { setTranscription("Capture failed: " + (e.response?.data?.detail || e.message)); }
    finally { setTranscribing(false); }
  };

  /* ── Auto-sync on reconnect ── */
  useEffect(() => {
    if (online && syncStatus === "local" && activeSession) {
      const timer = setTimeout(syncToServer, 3000);
      return () => clearTimeout(timer);
    }
  }, [online, activeSession]);

  /* ── Parse audit context from URL params (M3) ── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auditId = params.get("audit_id");
    if (auditId) {
      setAuditContext({
        audit_id: auditId,
        checklist_item_id: params.get("checklist_item_id"),
        standard: params.get("standard"),
        clause: params.get("clause"),
      });
    }
  }, []);

  /* ── Voice Recording (M3 - Whisper) ── */
  const startVoiceRecording = async () => {
    if (isReadOnly || !activeSession) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (e) {
      console.error("Mic access failed:", e);
      alert("Microphone access denied. Please allow microphone permissions.");
    }
  };

  const stopVoiceRecording = async () => {
    if (!mediaRecorderRef.current) return;
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);

    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 100) { resolve(); return; }

        setVoiceTranscribing(true);
        try {
          const formData = new FormData();
          formData.append("audio", blob, "voice_note.webm");
          formData.append("field_section", "notes");
          const res = await axios.post(
            `${API}/scriber/sessions/${activeSession.session_id}/voice`,
            formData,
            { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
          );
          setActiveSession(prev => ({ ...prev, input_count: (prev?.input_count || 0) + 1 }));
          // Show the transcript
          setTypedNote(prev => prev ? prev + "\n\n[Voice] " + res.data.data : "[Voice] " + res.data.data);
        } catch (e) {
          console.error("Voice transcription failed:", e);
          alert(e.response?.data?.detail || "Voice transcription failed");
        } finally {
          setVoiceTranscribing(false);
        }
        resolve();
      };
      mediaRecorderRef.current.stop();
    });
  };

  const downloadPage = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const link = document.createElement("a"); link.download = `scriber_page_${currentPageIdx + 1}.png`;
    link.href = canvas.toDataURL("image/png"); link.click();
  };

  const isReadOnly = activeSession?.status === "completed" || activeSession?.status === "closed";

  /* ═══════════════════════════════════════════════════════════════ */
  /*  DISCLOSURE MODAL                                              */
  /* ═══════════════════════════════════════════════════════════════ */
  if (showDisclosure) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6" data-testid="disclosure-gate">
        <div className="max-w-lg w-full bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-[#00C8E0]" />
            <div>
              <h2 className="text-lg font-bold text-white">SCRIBER AI Disclosure</h2>
              <p className="text-xs text-slate-400">Version {disclosureState.current_version || "1.0"}</p>
            </div>
          </div>
          <div className="bg-[#0A1628] rounded-lg p-4 mb-6 border border-[#1E3A5F]/50">
            <p className="text-sm text-slate-300 leading-relaxed" data-testid="disclosure-text">
              {disclosureState.disclosure_text || "SCRIBER uses AI-assisted extraction. All suggestions require human review before application to regulated records."}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={acceptDisclosure}
              className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-bold text-white transition-colors"
              data-testid="accept-disclosure-btn"
            >
              I Accept
            </button>
            <button
              onClick={() => { setShowDisclosure(false); setView("sessions"); }}
              className="px-4 py-2.5 bg-[#132340] hover:bg-[#1E3A5F] rounded-lg text-sm text-slate-400 transition-colors"
              data-testid="decline-disclosure-btn"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════ */
  /*  SESSION LIST VIEW                                             */
  /* ═══════════════════════════════════════════════════════════════ */
  if (view === "sessions") {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white p-6" data-testid="scriber-sessions-view">
        {/* Offline banner */}
        {!online && (
          <div className="mb-4 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-2" data-testid="offline-banner">
            <WifiOff className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400">You are offline. Sessions will sync when connection is restored.</span>
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">SCRIBER</h1>
              <p className="text-sm text-slate-400 mt-1">Workflow Capture Engine</p>
            </div>
            <button
              onClick={() => {
                const label = window.prompt("Session label (e.g. 'Audit Line 3 - PPE Check'):");
                if (label !== null) createSession(label || "New Session");
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1A6BCC] hover:bg-[#1558A8] rounded-lg text-sm font-bold transition-colors"
              data-testid="new-session-btn"
            >
              <Plus className="w-4 h-4" /> New Session
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading sessions...</div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-16">
              <Pen className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
              <p className="text-slate-400 text-sm">No sessions yet. Start a new capture session.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="session-list">
              {sessions.map(s => (
                <button
                  key={s.session_id}
                  onClick={() => openSession(s)}
                  className="w-full text-left p-4 bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-xl hover:border-[#1A6BCC]/40 transition-colors group"
                  data-testid={`session-${s.session_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${s.status === "active" ? "bg-emerald-400" : s.status === "paused" ? "bg-amber-400" : "bg-slate-500"}`} />
                      <div>
                        <div className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors">{s.label || "Untitled Session"}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {s.session_id} &middot; {s.input_count || 0} inputs &middot; {s.extraction_count || 0} extractions
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${STATUS_COLORS[s.status] || STATUS_COLORS.closed}`}>
                        {s.status}
                      </span>
                      <span className="text-[10px] text-slate-600">{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {s.workflow_context?.audit_id && (
                    <div className="mt-2 text-[10px] text-slate-500">
                      <Link className="w-3 h-3 inline mr-1" />Linked: {s.workflow_context.audit_id}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════ */
  /*  WORKSPACE VIEW                                                */
  /* ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-[#0A1628] text-white flex flex-col" data-testid="scriber-workspace">
      {/* Offline banner */}
      {!online && (
        <div className="px-4 py-1.5 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-2" data-testid="workspace-offline-banner">
          <WifiOff className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs text-amber-400">Offline — changes saved locally</span>
        </div>
      )}

      {/* Top toolbar */}
      <div className="border-b border-[#1E3A5F]/50 px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="workspace-toolbar">
        {/* Back button */}
        <button onClick={() => { setView("sessions"); loadSessions(); }} className="p-2 rounded bg-[#132340] hover:bg-[#1E3A5F] transition-colors" data-testid="back-to-sessions">
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Session info */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-white truncate max-w-[200px]">{activeSession?.label || "Session"}</span>
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${STATUS_COLORS[activeSession?.status] || ""}`}>
            {activeSession?.status}
          </span>
        </div>

        {/* Session controls */}
        <div className="flex gap-1">
          {activeSession?.status === "active" && (
            <button onClick={pauseSession} className="p-1.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-colors" data-testid="pause-session-btn" title="Pause">
              <Pause className="w-3.5 h-3.5" />
            </button>
          )}
          {activeSession?.status === "paused" && (
            <button onClick={resumeSession} className="p-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors" data-testid="resume-session-btn" title="Resume">
              <Play className="w-3.5 h-3.5" />
            </button>
          )}
          {activeSession?.status !== "completed" && activeSession?.status !== "closed" && (
            <button onClick={completeSession} className="p-1.5 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-colors" data-testid="complete-session-btn" title="Complete">
              <CheckCircle2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="w-px h-6 bg-[#1E3A5F]" />

        {/* Mode toggle */}
        <div className="flex bg-[#132340] rounded overflow-hidden border border-[#1E3A5F]">
          <button onClick={() => setMode("whiteboard")} className={`px-2.5 py-1 text-[10px] font-bold ${mode === "whiteboard" ? "bg-[#1A6BCC] text-white" : "text-slate-400 hover:text-white"}`} data-testid="mode-whiteboard">
            <Pen className="w-3 h-3 inline mr-0.5" /> Draw
          </button>
          <button onClick={() => setMode("form")} className={`px-2.5 py-1 text-[10px] font-bold ${mode === "form" ? "bg-[#1A6BCC] text-white" : "text-slate-400 hover:text-white"}`} data-testid="mode-form">
            <FileText className="w-3 h-3 inline mr-0.5" /> Form
          </button>
          <button onClick={() => setMode("notes")} className={`px-2.5 py-1 text-[10px] font-bold ${mode === "notes" ? "bg-[#1A6BCC] text-white" : "text-slate-400 hover:text-white"}`} data-testid="mode-notes">
            <Type className="w-3 h-3 inline mr-0.5" /> Notes
          </button>
        </div>

        {mode === "form" && (
          <div className="relative">
            <button onClick={() => setShowTemplatePicker(!showTemplatePicker)} className="flex items-center gap-1 px-2 py-1 bg-[#132340] border border-[#1E3A5F] rounded text-[10px] text-white" data-testid="template-picker-btn">
              <Layers className="w-3 h-3 text-[#00C8E0]" /> {FORM_TEMPLATES[formTemplate]?.name || "Template"}
            </button>
            {showTemplatePicker && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg shadow-xl z-50" data-testid="template-dropdown">
                {Object.values(FORM_TEMPLATES).map(tpl => (
                  <button key={tpl.id} onClick={() => { setFormTemplate(tpl.id); setShowTemplatePicker(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-[#132340] text-xs border-b border-[#1E3A5F]/30 last:border-0 ${formTemplate === tpl.id ? "bg-[#1A6BCC]/10" : ""}`}
                    data-testid={`template-${tpl.id}`}>
                    <div className="font-bold text-white">{tpl.name}</div>
                    <div className="text-[10px] text-slate-500">{tpl.desc}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {mode !== "notes" && (
          <>
            <div className="w-px h-6 bg-[#1E3A5F]" />
            {/* Pen tools */}
            <button onClick={() => setTool("pen")} className={`p-1.5 rounded ${tool === "pen" ? "bg-[#1A6BCC]" : "bg-[#132340] hover:bg-[#1E3A5F]"}`} data-testid="tool-pen"><Pen className="w-3.5 h-3.5" /></button>
            <button onClick={() => setTool("eraser")} className={`p-1.5 rounded ${tool === "eraser" ? "bg-[#1A6BCC]" : "bg-[#132340] hover:bg-[#1E3A5F]"}`} data-testid="tool-eraser"><Eraser className="w-3.5 h-3.5" /></button>
            <div className="flex gap-0.5">
              {PEN_COLORS.map(c => (
                <button key={c} onClick={() => { setPenColor(c); setTool("pen"); }}
                  className={`w-5 h-5 rounded-full border-2 ${penColor === c && tool === "pen" ? "border-white scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: c }} data-testid={`color-${c.replace("#", "")}`} />
              ))}
            </div>
            <button onClick={undo} disabled={undoStack.length === 0} className="p-1.5 rounded bg-[#132340] hover:bg-[#1E3A5F] disabled:opacity-30" data-testid="undo-btn"><Undo2 className="w-3.5 h-3.5" /></button>
            <button onClick={redo} disabled={redoStack.length === 0} className="p-1.5 rounded bg-[#132340] hover:bg-[#1E3A5F] disabled:opacity-30" data-testid="redo-btn"><Redo2 className="w-3.5 h-3.5" /></button>
            <button onClick={clearPage} className="p-1.5 rounded bg-[#132340] hover:bg-red-900/50" data-testid="clear-btn"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Sync status */}
          <div className="flex items-center gap-1 text-[10px]" data-testid="sync-status-badge">
            {online ? <Wifi className="w-3 h-3 text-emerald-400" /> : <WifiOff className="w-3 h-3 text-red-400" />}
            <span className={syncStatus === "synced" ? "text-emerald-400" : syncStatus === "syncing" ? "text-amber-400" : "text-slate-500"}>
              {syncStatus === "synced" ? "Synced" : syncStatus === "syncing" ? "Syncing..." : online ? "Local" : "Offline"}
            </span>
            {syncSummary?.conflict > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-[9px] font-bold" data-testid="conflict-badge">
                {syncSummary.conflict} conflicts
              </span>
            )}
          </div>

          <button onClick={syncToServer} disabled={syncing || !online} className="p-1.5 rounded bg-[#132340] hover:bg-[#1E3A5F] disabled:opacity-30" data-testid="sync-btn">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          {mode !== "notes" && (
            <button onClick={downloadPage} className="p-1.5 rounded bg-[#132340] hover:bg-[#1E3A5F]" data-testid="download-btn"><Download className="w-3.5 h-3.5" /></button>
          )}
          <button onClick={runExtraction} disabled={extracting || isReadOnly} className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-purple-600 hover:bg-purple-700 text-[10px] font-bold disabled:opacity-50" data-testid="extract-btn">
            {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Extract
          </button>
          <button onClick={() => setShowExtractions(!showExtractions)} className={`p-1.5 rounded ${showExtractions ? "bg-purple-600" : "bg-[#132340] hover:bg-[#1E3A5F]"}`} data-testid="toggle-extractions">
            <Eye className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas or Notes area */}
        {mode === "notes" ? (
          <div className="flex-1 flex flex-col p-4" data-testid="notes-area">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-[#00C8E0]" />
              <span className="text-xs font-bold text-white">Notes & Voice Capture</span>
              <span className="text-[10px] text-slate-500">({activeSession?.input_count || 0} inputs saved)</span>
              {auditContext && (
                <span className="text-[10px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded" data-testid="audit-context-badge">
                  <Link className="w-3 h-3 inline mr-1" />{auditContext.standard} {auditContext.clause}
                </span>
              )}
            </div>
            {/* Voice recording bar */}
            <div className="flex items-center gap-2 mb-3" data-testid="voice-controls">
              {!isRecording ? (
                <button
                  onClick={startVoiceRecording}
                  disabled={isReadOnly || voiceTranscribing}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-xs text-red-400 font-bold disabled:opacity-50 transition-colors"
                  data-testid="start-voice-btn"
                >
                  <Mic className="w-3.5 h-3.5" /> {voiceTranscribing ? "Transcribing..." : "Record Voice"}
                </button>
              ) : (
                <button
                  onClick={stopVoiceRecording}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-xs text-white font-bold animate-pulse transition-colors"
                  data-testid="stop-voice-btn"
                >
                  <Square className="w-3.5 h-3.5" /> Stop ({recordingTime}s)
                </button>
              )}
              {voiceTranscribing && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
              <span className="text-[10px] text-slate-600">Whisper AI transcription</span>
            </div>
            <textarea
              value={typedNote}
              onChange={(e) => setTypedNote(e.target.value)}
              placeholder={isReadOnly ? "Session completed — read only" : "Type or dictate your observations, findings, notes..."}
              disabled={isReadOnly}
              className="flex-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none resize-none disabled:opacity-50"
              data-testid="typed-note-input"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-[10px] text-slate-500">{typedNote.length} chars</span>
              <button
                onClick={saveTypedNote}
                disabled={!typedNote.trim() || savingNote || isReadOnly}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-xs font-bold disabled:opacity-50 transition-colors"
                data-testid="save-note-btn"
              >
                {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save Input
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 relative" data-testid="canvas-area">
            <canvas
              ref={canvasRef}
              className="w-full h-full cursor-crosshair touch-none"
              style={{ touchAction: "none" }}
              onPointerDown={startDraw}
              onPointerMove={draw}
              onPointerUp={endDraw}
              onPointerLeave={endDraw}
              data-testid="scriber-canvas"
            />
            {isReadOnly && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <span className="px-4 py-2 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg text-sm text-slate-400">Session completed — read only</span>
              </div>
            )}
            {/* Page nav */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#0D1F38]/90 border border-[#1E3A5F] rounded-full px-3 py-1.5" data-testid="page-nav">
              <button onClick={() => currentPageIdx > 0 && goPage(currentPageIdx - 1)} disabled={currentPageIdx === 0} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid="prev-page">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-[10px] font-mono text-white">{currentPageIdx + 1}/{pages.length}</span>
              <button onClick={() => currentPageIdx < pages.length - 1 && goPage(currentPageIdx + 1)} disabled={currentPageIdx === pages.length - 1} className="text-slate-400 hover:text-white disabled:opacity-30" data-testid="next-page">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-3 bg-[#1E3A5F]" />
              <button onClick={addPage} disabled={isReadOnly} className="text-[#00C8E0] hover:text-white disabled:opacity-30" data-testid="add-page"><Plus className="w-3.5 h-3.5" /></button>
              {mode !== "notes" && (
                <>
                  <div className="w-px h-3 bg-[#1E3A5F]" />
                  <button onClick={transcribePage} disabled={transcribing || isReadOnly} className="text-emerald-400 hover:text-white disabled:opacity-30" data-testid="transcribe-btn" title="Capture handwriting">
                    {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Extraction review panel */}
        {showExtractions && (
          <div className="w-80 border-l border-[#1E3A5F]/50 bg-[#0D1F38] flex flex-col" data-testid="extraction-panel">
            <div className="px-4 py-3 border-b border-[#1E3A5F]/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-bold text-white">Extractions</span>
                <span className="text-[10px] text-slate-500">({extractions.length})</span>
              </div>
              <button onClick={() => setShowExtractions(false)} className="text-slate-400 hover:text-white" data-testid="close-extractions"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {extractions.length === 0 ? (
                <div className="p-4 text-xs text-slate-500">No extractions yet. Add inputs and click "Extract".</div>
              ) : extractions.map(ext => (
                <ExtractionCard key={ext.extraction_id} extraction={ext} onReview={reviewExtraction} isReadOnly={isReadOnly} />
              ))}
            </div>
          </div>
        )}

        {/* Transcription panel */}
        {showTranscription && !showExtractions && (
          <div className="w-72 border-l border-[#1E3A5F]/50 bg-[#0D1F38] flex flex-col" data-testid="transcription-panel">
            <div className="px-4 py-3 border-b border-[#1E3A5F]/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-[#00C8E0]" />
                <span className="text-xs font-bold text-white">Capture Status</span>
              </div>
              <button onClick={() => setShowTranscription(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 text-sm text-slate-300">{transcription || "Click capture to record handwriting."}</div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Extraction Card Component ───────────────────────────── */
const ExtractionCard = ({ extraction, onReview, isReadOnly }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(extraction.extracted_text || "");

  const statusColors = {
    suggested: "text-amber-400 bg-amber-500/10",
    accepted: "text-emerald-400 bg-emerald-500/10",
    edited: "text-blue-400 bg-blue-500/10",
    rejected: "text-red-400 bg-red-500/10",
    applied: "text-purple-400 bg-purple-500/10",
  };

  return (
    <div className="border-b border-[#1E3A5F]/30 p-3" data-testid={`extraction-${extraction.extraction_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${statusColors[extraction.status] || ""}`}>
            {extraction.status}
          </span>
          <span className="text-[10px] text-slate-500">{(extraction.confidence * 100).toFixed(0)}% conf</span>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-white">
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      <p className={`text-xs text-slate-300 ${expanded ? "" : "line-clamp-3"}`}>
        {extraction.extracted_text}
      </p>

      {expanded && extraction.mapped_fields && Object.keys(extraction.mapped_fields).length > 0 && (
        <div className="mt-2 space-y-1">
          {Object.entries(extraction.mapped_fields).map(([key, val]) => (
            <div key={key} className="text-[10px]">
              <span className="text-slate-500 uppercase">{key}:</span>
              <span className="text-slate-300 ml-1">
                {Array.isArray(val) ? val.join(", ") : String(val)}
              </span>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          className="w-full mt-2 bg-[#0A1628] border border-[#1E3A5F] rounded p-2 text-xs text-white resize-none h-20 focus:border-[#1A6BCC] focus:outline-none"
          data-testid="edit-extraction-text"
        />
      )}

      {extraction.status === "suggested" && !isReadOnly && (
        <div className="flex gap-1.5 mt-2">
          <button onClick={() => onReview(extraction.extraction_id, "accepted")} className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 rounded text-[10px] text-emerald-400 font-bold" data-testid="accept-extraction-btn">
            <ThumbsUp className="w-3 h-3" /> Accept
          </button>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 rounded text-[10px] text-blue-400 font-bold" data-testid="edit-extraction-btn">
              <Edit3 className="w-3 h-3" /> Edit
            </button>
          ) : (
            <button onClick={() => { onReview(extraction.extraction_id, "edited", editText); setEditing(false); }} className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 rounded text-[10px] text-blue-400 font-bold" data-testid="save-edit-btn">
              <Save className="w-3 h-3" /> Save
            </button>
          )}
          <button onClick={() => onReview(extraction.extraction_id, "rejected")} className="flex items-center gap-1 px-2 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-[10px] text-red-400 font-bold" data-testid="reject-extraction-btn">
            <ThumbsDown className="w-3 h-3" /> Reject
          </button>
        </div>
      )}

      {extraction.status === "accepted" && !isReadOnly && (
        <div className="mt-2 text-[10px] text-emerald-400">
          <CheckCircle2 className="w-3 h-3 inline mr-1" />
          Ready for binding — use Audit Conductor to apply to records
        </div>
      )}

      {extraction.status === "applied" && (
        <div className="mt-2 text-[10px] text-purple-400">
          <Link className="w-3 h-3 inline mr-1" /> Applied to regulated record
        </div>
      )}
    </div>
  );
};

export default ScriberPage;
